import React from 'react';
import {StartButton} from "../buttons/startbutton";
import {SwapButton} from "../buttons/swapButton";
import {NoSwapButton} from "../buttons/noSwapButton";
import allSortLists from './practice_lists.json';
import { bubblesort } from './bubblesort';
import ErrorFeedback from './feedback/errorFeedback';
import FinishedListFeedback from './feedback/finishedListFeedback';
import { CloseFeedbackButton } from '../buttons/closeFeedbackButton';
import NotEnoughButtonsFeedback from './feedback/notEnoughtButtonsFeedback';
import { SortNumberButton } from '../buttons/sortNumberButton';
import { CheckIfFinishedButton } from '../buttons/checkIfFinishedButton';

/**
 * @author: Alisa Münsterberg
 * @date: 2022-10-14
 */

export default class Practice extends React.Component{
    
    constructor(props){
        super(props);
        this.state = {
            completeSortingList : bubblesort(allSortLists[Math.floor(Math.random() * allSortLists.length)]),
			stepCounter : 0,
            selectedButtons : [],
			lastSelectedButtons : [],
			lastButton : 0,
            error : false,
            errorType : "",
            queriedFinished: false,
            reallyFinished: false,
            notEnoughButtons: false,
            standardButtonColour : '#b578ee',
            selectedButtonColour : 'blueviolet',
        }
        this.checkSteps = this.checkStep.bind(this);
        this.errorIdentification = this.errorIdentification.bind(this);
        this.handleButtonSelection = this.handleButtonSelection.bind(this);
        this.checkFinished = this.checkFinished.bind(this);
        this.changeButtonColour = this.changeButtonColour.bind(this);
        this.reset = this.reset.bind(this);
    }

    reset() {
        this.setState({
            completeSortingList : bubblesort(allSortLists[Math.floor(Math.random() * allSortLists.length)]),
			stepCounter : 0,
            selectedButtons : [],
			lastSelectedButtons : [],
			lastButton : 0,
            error : false,
            errorType : "",
            queriedFinished: false,
            reallyFinished: false,
            notEnoughButtons: false,
            standardButtonColour : '#b578ee',
            selectedButtonColour : 'blueviolet',
        })
    }
    

    /*
    * Changes the colouring of the button specified by its identifying key to the input colour.
    */
    changeButtonColour(key, colour) {
        const button = document.getElementById(key);
        button.style.backgroundColor = colour;
    }

    /*
    * Stores buttons as selected and changes their colours to indicate this, whenever the user clicks a SortNumberButtons.
    * A maximum of two buttons can be selected at the same time.
    */
    handleButtonSelection(key,nr){
        this.setState({notEnoughButtons : false});
        if(this.state.selectedButtons.length === 0){
            this.setState({selectedButtons : [[key,nr]]});
            this.changeButtonColour(key, this.state.selectedButtonColour);
        }
        else{
            this.setState({selectedButtons : this.state.selectedButtons[1] = [[key,nr]]});  
            this.changeButtonColour(key, this.state.selectedButtonColour);
            this.setState({lastSelectedButtons : JSON.parse(JSON.stringify( this.state.selectedButtons))});	
			this.changeButtonColour(this.state.lastSelectedButtons[0][0], this.state.standardButtonColour);
			this.changeButtonColour(key, this.state.selectedButtonColour); //ensures that a button stays selected even if the same button is repeatedly clicked
        }
    }

    /*
    * Checks whether the currently displayed list is already sorted when the user clicks the CheckIfFinishedButton
    * and activates display of FinishedListFeedback accordingly.
    */
    checkFinished(){
        this.setState({queriedFinished : true});
        let finishedList = this.state.completeSortingList[0][this.state.completeSortingList[0].length-1];
        if(JSON.stringify(this.state.completeSortingList[0][this.state.stepCounter]) === JSON.stringify(finishedList)){
            this.setState({reallyFinished : true});
        } else{
            this.setState({reallyFinished : false});
    }}
     

    /*
    * Identifies the type of error committed in case the users solution for a step was incorrect.
    * The following error types are identified:
    * Type 1 (code 0): The user swapped the two selected elements but they should not be swapped.
    * Type 2 (code 1): The user did not swap the two selected elements but they should be swapped.
    * Type 3 (code 2): The  user should have chosen elements from the very beginning of the list but did not.
    * Type 4 (code 3): The chosen elements are not direct neighbours.
    * Type 5 (code 4): The chosen elements are not correct for the next step but the one after that, i.e. the steps don't overlap.
    * Type 6 (code 5): One or more elements were chosen from the sorted end of the list that should not be interacted with. 
    * Unspecified error (code 6): The user chose the wrong buttons but none of the specified error types matched the error.
    * 
    * Type 6 is tested for before type 3 as it always contains type 3 but in cases of co-occurence diagnosing type 6
    * is more accurate to represent the underlying misconception.
    * 
    * Types 1 and 2 are classified as action errors, the other types and unspecified category as position errors.
    */
    errorIdentification(button1, button2, swap, areIndicesCorrect, correctIndices){
        const correct1 = correctIndices[0];
        const correct2 = correctIndices[1];
        const firstButtonIndex = button1[0];
        const secondButtonIndex = button2[0];
		
        this.setState({error : true});
        //diagnosis of action errors
		if(areIndicesCorrect){
			if(swap === true && button1[1] < button2[1]){
                this.setState({errorType : 0});
			}

			else if(swap !== true && button1[1] > button2[1]){
                this.setState({errorType : 1});
			}
		}
        //diagnosis of position errors
        else{
            const loop = this.state.completeSortingList[2][this.state.stepCounter+1];
            const sortedThreshold = this.state.completeSortingList[0][0].length - (loop + 1);
			
            if(firstButtonIndex > sortedThreshold || secondButtonIndex > sortedThreshold ){
                this.setState({errorType : 5});
            }

			else if(correct1 === 0){
                    this.setState({errorType : 2});
            }
				
            else if(secondButtonIndex !== firstButtonIndex+1){
                this.setState({errorType : 3});
            }

            else if(button1[0] === correct2){
                this.setState({errorType : 4});
            }
			
            else{
                this.setState({errorType : 6});
            }
        }
    }

    /*
    * Takes two buttons and returns them ordered according to their position in the list in ascending order.
    */
    sortButtons(firstButton, secondButton, button1, button2) {
        if (firstButton[0] < secondButton[0]) {
            button1 = firstButton;
            button2 = secondButton;
        } else {
            button1 = secondButton;
            button2 = firstButton;
        }
        return { button1, button2 };
    }


    /*
    * Checks whether the user chose the correct action and elements in the current sorting step when they end the step by
    * clicking the SwapButton or NoSwapButton.
    * Activates the errorIdentification method to identify the concrete type of error if the user solution to the step is incorrect,
    * activates display of NotEnoughButtonsFeedback if less than two buttons were selected and closes old feedback.
    */
    checkStep(swap){
        if(this.state.lastSelectedButtons.length < 2){
            this.setState({notEnoughButtons : true});
        }
        const displaylist = this.state.completeSortingList[0][this.state.stepCounter];
        const correctIndices = this.state.completeSortingList[1][this.state.stepCounter]; 
        const correctList = this.state.completeSortingList[0][this.state.stepCounter+1]; //correct step solution 
        let testList = displaylist; 
        const firstButton = this.state.lastSelectedButtons[0]; 
        const secondButton = this.state.lastSelectedButtons[1][0];  
        let areIndicesCorrect = true;
        var button1 = [];
        var button2 = [];

        this.setState({queriedFinished : false}); 
        this.setState({error : false});
        
        ({ button1, button2 } = this.sortButtons(firstButton, secondButton, button1, button2));
        
        //in case the SwapButton was clicked, swap buttons to reflect user solution
        if(swap === true){
            const newList = JSON.parse(JSON.stringify(displaylist));
            newList[button1[0]] = button2[1];
            newList[button2[0]] = button1[1];
            testList = newList;
        }
        
        //test if positions were chosen correctly
        if(button1[0] !== correctIndices[0]){
            areIndicesCorrect = false;
        }
        if(button2[0] !== correctIndices[1]){
            areIndicesCorrect = false;
        }
        
        // activate errorIdentification if user and correct solution don't match or wrong buttons were chosen
        if(JSON.stringify(testList) !== JSON.stringify(correctList) || !areIndicesCorrect){
            this.errorIdentification(button1, button2, swap, areIndicesCorrect, correctIndices);
        } 
        else{
            this.setState({lastButton : this.state.completeSortingList[1][this.state.stepCounter][1]+1})
            this.setState({stepCounter: this.state.stepCounter + 1}); //increase stepCounter 
            this.setState({selectedButtons: []}); 
            this.setState({lastSelectedButtons: []}); 
            this.changeButtonColour(button1[0], this.state.standardButtonColour); 
            this.changeButtonColour(button2[0], this.state.standardButtonColour);
        }
    }
 
    /*
    * Renders the Practice component with StartButton, CheckIfFinishedButton, a text of hints,
    * the current iteration count and position of the last chosen button,
    * SortNumberButtons containing the list for sorting and a SwapButton and NoSwapButton.
    * Activates feedback according to respective states.
    * The component is rendered when the user clicks a PracticeButton or loads the app with its path extension.
    */
    render(){
        return(
            <div>
                    <div className='App-buttons'>
                        <StartButton/>
                        <CheckIfFinishedButton onClick={this.checkFinished}/>
                    </div>
                    <div className='Practice'> 
                    <div> 
                        <div className='textbox'> 
                            Tipp: <br/>Klicke auf die beiden Zahlen, die du mit Bubblesort als nächstes auswählst. <br/>
                            Wenn du die richtigen Zahlen gefunden hast, klicke auf "Tauschen" oder "Nicht Tauschen", <br/> 
                            je nachdem, was der richtige Schritt ist. <br/>
						</div>
						<h5>
							So viele Durchgänge hast du schon durchgeführt: {(this.state.completeSortingList[2][this.state.stepCounter] === 0) ? this.state.completeSortingList[2][this.state.stepCounter] : this.state.completeSortingList[2][this.state.stepCounter] -1 } <br/>
							Du hast dir als letztes diese Position angeschaut: {this.state.lastButton}
                        </h5>
                        <div className="App-buttons">
                        {this.state.completeSortingList[0][this.state.stepCounter].map((nr,index) =>   
                            <SortNumberButton key={index} id={index} value= {nr} onClick={() => this.handleButtonSelection(index,nr)}>         
                                {nr}
                            </SortNumberButton>       )}     
                        </div>
                        <div className='App-buttons'>
                            <SwapButton onClick={() => this.checkStep(true)}/>
                            <NoSwapButton onClick={() => this.checkStep(false)}/>
                        </div>
                    </div>
                    <div className='feedbackbox'> 
                        {this.state.error &&
                         <ErrorFeedback className='feedbackbox' list={this.state.completeSortingList[0][this.state.stepCounter]} 
                            errorType= {this.state.errorType} previousList={(this.state.stepCounter > 0) ? this.state.completeSortingList[0][this.state.stepCounter-1] : []}
                           trueIndices= {this.state.completeSortingList[1][this.state.stepCounter]} loop={this.state.completeSortingList[2][this.state.stepCounter]}/>}
                        {this.state.error &&
                            <CloseFeedbackButton onClick={() => this.setState({error : false})}/>
                            }

                        {this.state.queriedFinished &&
                           <FinishedListFeedback finished = {this.state.reallyFinished} reset={this.reset}/>}
                        {(this.state.queriedFinished && this.state.reallyFinished === false) &&
                            <CloseFeedbackButton onClick={() => this.setState({queriedFinished : false})}/>
                            }
                        {this.state.stepCounter === this.state.completeSortingList[0].length-1
                            &&
                            <FinishedListFeedback trulyFinished = {true}  reset={this.reset}/>
                        }

                        {this.state.notEnoughButtons &&
                            <NotEnoughButtonsFeedback />
                        }
                        {this.state.notEnoughButtons &&
                            <CloseFeedbackButton onClick={() => this.setState({notEnoughButtons : false})}/>
                            }

                        
                   </div>
                </div>
                </div>
        )};
}